import {
  SHIPPING_OPTION_EXPRESS,
  SHIPPING_OPTION_FREE,
  SHIPPING_OPTION_PRIORITY,
} from './shipping.consts'
import { type ShippingOption } from './shipping.types'
import { logger } from '../../services/logger'
import { getTileStyleData } from '../../services/tilesStyling'
import { getTileMaterialType } from '../../services/tileMaterial'
import { getTileSize } from '../../components/PhotoBank/tileUtils'
import { FRAME_COLORS } from '../../core/Tile/consts'
import { isFramedMaterial } from '../../utils/materialTypeUtils'
import { MATERIAL_TYPES } from '@mixtiles/web-backend-shared'
import { type Tile } from '../../components/TilesProvider/TilesProvider.types'
import {
  formatIsoDateRange,
  isoToDeliveryDate,
} from '../../utils/DateTimeUtils'

export function getShippingOptionPricing(shippingOption: any) {
  // currencyCode may only appear in pricing for a short period of time until the backend is also deployed (and then this can be removed)
  return shippingOption.pricing.currencyCode
    ? shippingOption.pricing
    : Object.values(shippingOption.pricing)[0]
}

export function isOptionExpress(shippingOption: ShippingOption) {
  return shippingOption?.shippingOptionName === SHIPPING_OPTION_EXPRESS
}

export function isOptionPriority(shippingOption: ShippingOption) {
  return shippingOption?.shippingOptionName === SHIPPING_OPTION_PRIORITY
}

/**
 * Calculate range of dates for shipping options based on an end date and a number
 * of days before. Adjust the date if it falls on a weekend.
 * @param shippingOption The shipping option (express or standard)
 */
export function calculateShippingRange(shippingOption: ShippingOption) {
  const endDateISO = shippingOption.promisedArrivalDate.iso
  const startDateISO = shippingOption.minPromisedArrivalDateMoment?.iso
  if (!startDateISO) {
    logger.warning(
      'minPromisedArrivalDateMoment is missing in shippingOption',
      { shippingOption }
    )
  }
  return { startDateISO, endDateISO }
}

export function shouldShowDeliveryRange(deliveryPromise: ShippingOption) {
  // Only show range for free shipping
  return deliveryPromise.shippingOptionName === SHIPPING_OPTION_FREE
}

export function getDeliveryDateDescription(
  translate: (key: string, options?: Record<string, string>) => string,
  deliveryPromiseOption: ShippingOption,
  isDefaultOption = true
) {
  const deliveryRange = calculateShippingRange(deliveryPromiseOption)
  const dateRangeText =
    deliveryRange?.startDateISO &&
    shouldShowDeliveryRange(deliveryPromiseOption)
      ? translate('order.checkout.shipping_options.estimated_delivery', {
          v1: formatIsoDateRange(
            deliveryRange.startDateISO,
            deliveryRange.endDateISO
          ),
        })
      : translate('order.checkout.shipping_options.delivery_by', {
          v1: isoToDeliveryDate(
            deliveryPromiseOption.promisedArrivalDate.iso,
            'mmm d'
          ),
        })

  return isDefaultOption
    ? `${translate(
        'order.checkout.payment_summary_rows.shipping'
      )} (${dateRangeText})`
    : dateRangeText
}

/**
 * Returns the necessary information about the order's tiles for an accurate delivery promise calculation
 */
export interface PartialUploadInformation {
  tileSize: string
  tileType: 'classic' | 'framed'
  frameColor: string
  materialType: string
}

export function getPartialUploadsInformation({
  orderItems,
}: any): PartialUploadInformation[] {
  return orderItems
    .map((orderItem: any) => {
      return orderItem.orderTiles.map((tile: Tile) => {
        const frameColor =
          getTileStyleData(tile).frameColor || tile?.style?.color // tile?.style?.color is used for gallery walls
        const materialType = getTileMaterialType(tile)
        return {
          tileSize: getTileSize(tile),
          tileType:
            frameColor === FRAME_COLORS.NONE || !isFramedMaterial(materialType)
              ? 'classic'
              : 'framed',
          frameColor: isFramedMaterial(materialType)
            ? frameColor
            : FRAME_COLORS.NONE,
          materialType:
            materialType === MATERIAL_TYPES.FRAMELESS
              ? MATERIAL_TYPES.CLASSIC
              : materialType,
        }
      })
    })
    .flat()
}
